import isEmpty from 'lodash/isEmpty';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { useHistory, useParams } from 'react-router-dom';

import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider';

import { KFA_LAYOUTS } from '../../../common/components/LayoutsRightPanel/LayoutTab/Layouts';
import {
  RTEContextProvider,
  SectionsProvider,
} from '../../../common/components/SectionLayout/SectionContext';
import insertChartAndTable from '../../../common/helpers/insertMinitableEventListener';
import updateEntityVersion from '../../../common/actions/updateEntityVersion';
import { useCurrentUser } from '../../../common/hooks/useCurrentUser';
import { viewHypothesisPath } from '../../../common/services/urlService';
import { EmptyClipboardModal } from '../../../common/components/EmptyClipboardModal';

import useLinkedNarratives from '../../narratives/EditConnectedContent/useLinkedNarratives';
import { getProject } from '../../project/projectSlice';
import {
  getHypothesisError,
  getHypothesisLoading,
  loadKfa,
  loadKfaVersions,
  resetHypothesis,
  selectClientVersion,
  selectDraftVersion,
  selectHypothesisVersions,
  selectKfa,
} from '../hypothesisSlice';
import {
  initialSections,
  loadLibraryItems,
  prepareLibraryItems,
} from '../libraryItemsSupports';
import EditHypothesis from './EditHypothesis';
import useIsMountedRef from '../../../common/hooks/useIsMountedRef';
import LoadingError from '../../../common/components/LoadingError';
import ResponsiveLoader from '../../../common/components/ResponsiveLoader/ResponsiveLoader';
import { ContextRoot } from '../../../common/contexts/Root';

const EditHypothesisWithSections = () => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const isMountedRef = useIsMountedRef();
  const { bootStrap } = useContext(ContextRoot);
  const { projectId, hypothesisId: hypothesisContentThreadId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const refHandlerSaveForm = useRef();

  const [connectedContentIsChanged, setConnectedContentIsChanged] =
    useState(false);

  const [currentlyVisibleConnections, setCurrentlyVisibleConnections] =
    useState(null);

  const [errorType, setErrorType] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [preparedLibraryItems, setPreparedLibraryItems] = useState({});

  const project = useSelector(getProject);
  const hasError = useSelector(getHypothesisError);
  const isLoading = useSelector(getHypothesisLoading);

  const versions = useSelector(selectHypothesisVersions);
  const draftVersion = useSelector(selectDraftVersion);
  const clientVersion = useSelector(selectClientVersion);

  const currentUser = useCurrentUser(project);

  const ref = useRef(null);
  const isLoadKfaCalled = useRef(false);

  const rawKfa = useSelector(selectKfa);
  const kfa = (rawKfa || { content: undefined }).content;

  const [thumbnail, setThumbnail] = useState();
  const [loadingThumbnail, setLoadingThumbnail] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contextStatusInfoItem = useMemo(
    () => ({
      shareWithAll: false,
      versionStatus: kfa?.versionStatus,
      userViewStatus: kfa?.userViewStatus,
      isEditMode: true,
      isViewMode: false,
    }),
    [kfa]
  );

  useEffect(() => {
    if (!(rawKfa && rawKfa.libraryItemGroups)) {
      return;
    }

    const refS = JSON.stringify(ref.current);
    const kfaS = JSON.stringify(rawKfa);

    if (refS !== kfaS) {
      ref.current = rawKfa;
    } else return;

    setLoadingThumbnail(true);
    const {
      libraryItemGroups,
      sharedLibraryItems,
      items,
      content: { insightWithItems },
    } = rawKfa;
    (async () => {
      const preparedItems = await prepareLibraryItems({
        projectId,
        libraryItemGroups,
        sections: [insightWithItems],
        sharedLibraryItems: sharedLibraryItems || items,
        contextStatusInfoItem,
        isPwcUser,
      });
      setPreparedLibraryItems({ ...preparedItems, loaded: true });
      const newInsight = await loadLibraryItems({
        projectId,
        libraryItemGroups,
        sections: [insightWithItems],
        sharedLibraryItems: sharedLibraryItems || items,
        contextStatusInfoItem,
        ...preparedItems,
        caller: 'EditHypothesisWithSections',
      });
      setThumbnail((newInsight[0] || { items: [] }).items[0]);
      setLoadingThumbnail(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, rawKfa, isPwcUser]);

  const setConnectedContentChanged = () => {
    setConnectedContentIsChanged(!connectedContentIsChanged);
  };

  useEffect(() => {
    dispatch(
      loadKfaVersions(projectId, hypothesisContentThreadId, false, isMountedRef)
    );
  }, [dispatch, projectId, hypothesisContentThreadId, isMountedRef]);

  const getDraftContent = useCallback(() => {
    if (!isMountedRef.current || isLoadKfaCalled.current) return;
    const { contentId: contentIdVersion } = draftVersion;
    isLoadKfaCalled.current = true;
    dispatch(loadKfa(projectId, contentIdVersion, false, isMountedRef));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftVersion, projectId, isMountedRef]);

  const getSharedContent = useCallback(() => {
    if (!isMountedRef.current || isLoadKfaCalled.current) return;
    const { contentId: contentIdVersion } = clientVersion;
    isLoadKfaCalled.current = true;
    dispatch(loadKfa(projectId, contentIdVersion, false, isMountedRef));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVersion, projectId, isMountedRef]);

  useEffect(() => {
    if (versions) {
      if (draftVersion && !isEmpty(draftVersion)) {
        getDraftContent();
        dispatch(updateEntityVersion(draftVersion.versionNumber));
      } else if (clientVersion && !isEmpty(clientVersion)) {
        getSharedContent();
        dispatch(updateEntityVersion(clientVersion.versionNumber));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify({ draftVersion, projectId, versions, clientVersion })]);

  const gotoKfaView = () => {
    history.replace(viewHypothesisPath(projectId, hypothesisContentThreadId));
    dispatch(resetHypothesis());
  };

  const updateThumbnail = (newThumbnail) => {
    setThumbnail(newThumbnail);
  };

  const linkedNarrativesChanged = useLinkedNarratives(
    currentlyVisibleConnections,
    kfa ? kfa.contentId : null,
    connectedContentIsChanged
  );

  const [deactivateCallback, setDeactivateCallback] = useState(null);
  const [noDataOnClipboard, setNoDataOnClipboard] = useState(false);
  const darkMode = useSelector((state) => state.userSettings.settings.darkMode);

  const setEventListener = useCallback(
    (editor) => {
      const editorContainer = document.getElementById('rich-text-editor');
      if (deactivateCallback) {
        deactivateCallback();
        setDeactivateCallback(null);
      }
      if (editorContainer && editor) {
        const removeListener = insertChartAndTable({
          bootStrap,
          contextStatusInfoItem,
          darkMode,
          dispatch,
          editor,
          isPwcUser,
          editorContainer,
          onChangeState({ type }) {
            if (type === 'success') {
              refHandlerSaveForm.current?.();
            }
          },
          projectId,
          setNoDataOnClipboard,
        });

        setDeactivateCallback(() => {
          return () => {
            removeListener();
          };
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      bootStrap,
      contextStatusInfoItem,
      darkMode,
      deactivateCallback,
      dispatch,
      projectId,
      isPwcUser,
    ]
  );

  if (hasError) {
    return <LoadingError />;
  }

  if (isLoading) {
    return <ResponsiveLoader />;
  }

  if (!kfa || !currentUser) {
    return null;
  }

  const initialValidSections = kfa.sections.length
    ? kfa.sections
    : initialSections;

  return (
    <SectionsProvider
      value={{
        initialSections: initialValidSections,
        rightPanelLayoutType: KFA_LAYOUTS,
      }}
    >
      <SyncedInfoProvider value={contextStatusInfoItem}>
        <RTEContextProvider
          value={{
            setEditor: setEventListener,
          }}
        >
          {noDataOnClipboard && (
            <EmptyClipboardModal
              show={noDataOnClipboard}
              onHide={() => setNoDataOnClipboard(false)}
            />
          )}
          <div id="rich-text-editor" />

          <EditHypothesis
            currentlyVisibleConnections={currentlyVisibleConnections}
            errorType={errorType}
            gotoHypothesisView={gotoKfaView}
            isSaving={isSaving}
            kfa={kfa}
            linkedNarrativesChanged={linkedNarrativesChanged}
            loadingThumbnail={loadingThumbnail}
            preparedLibraryItems={preparedLibraryItems}
            projectId={projectId}
            rawKfa={rawKfa}
            refHandlerSaveForm={refHandlerSaveForm}
            setConnectedContentChanged={setConnectedContentChanged}
            setCurrentlyVisibleConnections={setCurrentlyVisibleConnections}
            setErrorType={setErrorType}
            setIsSaving={setIsSaving}
            thumbnail={thumbnail}
            updateThumbnail={updateThumbnail}
          />
        </RTEContextProvider>
      </SyncedInfoProvider>
    </SectionsProvider>
  );
};

export default EditHypothesisWithSections;
